import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table'; // IMPORTANTE
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { UserAlertService } from '../../services/user-alert.service';
import {
  ActiveGamerName,
  GamerNameType,
  PreviousGamerNameInfo,
  UserAlertInfo,
  UserDetail,
  TransactionResponse,
  AlertType,
  CreateAlertPayload,
  ListAlertResponse
} from '../../models/user-alert';
import { Transaction } from '../../models/transaction';
import { KYC } from '../../models/kyc';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';
import { AdminStatusBadgeComponent } from '../shared/admin-status-badge/admin-status-badge.component';
import { AccountService } from '../../services/account.service';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    AdminPageHeaderComponent,
    AdminStatusBadgeComponent,
    TableModule,
    ProgressSpinnerModule,
    TooltipModule,
    DropdownModule,
  ],
  providers: [MessageService],
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
})
export class UserDetailComponent implements OnInit {
  private readonly RIOT_TYPE: GamerNameType = 'RIOT_GAMES';
  private readonly STEAM_TYPE: GamerNameType = 'STEAM';

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  user: UserDetail | null = null;
  isLoading = false;
  errorMessage: string | null = null;
  originalEmail: string = '';
  editableEmail: string = '';
  displayBalanceModal = false;
  filterConversion = false;
  filterBattlePrize = false;
  filterBattleCost = false;
  filterBattlePassReward = false;
  filterHeroPurchase = false;
  filterCashOut = false;
  filterDeposits = false;
  currentPage = 1;
  itemsPerPage = 20;
  previousPageBeforeFilter = 1;
  transactions: Transaction[] = [];
  userBalance: { coins: number; diamonds: number } | null = null;
  isLoadingTransactions = false;
  totalItems = 0;
  displayNoteModal = false;
  editingNoteId: number | null = null;
  newNoteText = '';
  isSavingNote = false;
  kycList: KYC[] = [];
  isLoadingKYC = false;
  isLoadingAlert = false;
  displayCreateAlertModal = false;
  isSubmittingAlert = false;

  alertTypesFromDatabase: AlertType[] = [];
  alertTypeOptions: { label: string, value: string }[] = [];
  newAlert: CreateAlertPayload = {
    alert_code: '',
    game: '',
    description: '',
    alert_description: '',
    alert_risk: 1
  };
  gameOptions = [
    { label: 'LOL', value: 'LEAGUE_OF_LEGENDS' },
    { label: 'CS', value: 'COUNTER_STRIKE' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userAlertService: UserAlertService,
    private messageService: MessageService,
    private accountService: AccountService,
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      const userId = parseInt(idParam, 10);

      this.fetchUserDetails(userId);
      this.fetchKYCVerifications(userId);
      this.loadAlertTypes();
    } else {
      this.errorMessage = 'ID de usuário não encontrado.';
    }
  }

  fetchUserDetails(id: number): void {
    this.isLoading = true;
    this.userAlertService.retrieveUser(id).subscribe({
      next: (data) => {
        if (data.birthdate) {
          data.birthdate = new Date(data.birthdate);
        }
        if (data.created_at) {
          data.created_at = new Date(data.created_at);
        }
        this.user = data;
        this.originalEmail = data.email;
        this.editableEmail = data.email;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = 'Falha ao buscar os detalhes do usuário.';
        this.isLoading = false;
      },
    });
  }

  fetchKYCVerifications(id: number): void {
    this.isLoadingKYC = true;
    this.accountService.getKYCVerifications(id).subscribe({
      next: (data) => {
        this.kycList = data.sort((a, b) => {
          const activeA = a.is_active ? 1 : 0;
          const activeB = b.is_active ? 1 : 0;

          if (activeA !== activeB) {
            return activeB - activeA;
          }
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        });
        this.isLoadingKYC = false;
      },
      error: (err) => {
        this.errorMessage = 'Falha ao buscar as verificações KYC do usuário.';
        console.error('Erro ao buscar KYC:', err);
        this.isLoadingKYC = false;
      },
    });
  }

  toggleInfluencerStatus(): void {
    if (!this.user) return;

    const id = this.user.id;

    const newStatus = !this.user.is_influencer;
    this.userAlertService.updateInfluencerStatus(this.user.id, newStatus).subscribe({
      next: (updatedUser) => {
        this.user = updatedUser;
        alert(
          `Conta do usuário ${this.user.username} foi ${newStatus ? 'incluida' : 'excluida'} como influencer com sucesso!`,
        );
        this.fetchUserDetails(id);
      },
      error: (err) => {
        alert('Falha ao atualizar o status da conta.');
      },
    });
  }

  unlinkGamerName(type: GamerNameType): void {
    if (!this.user) {
      return;
    }

    const activeTag =
      type === this.RIOT_TYPE
        ? this.riotActiveGamerName
        : this.steamActiveGamerName;
    if (!activeTag) {
      return;
    }

    if (
      confirm(
        `Tem certeza que deseja desvincular o gamertag "${activeTag.gamer_name}" deste usuário?`,
      )
    ) {
      this.userAlertService.unlinkGamerName(this.user.id, type).subscribe({
        next: () => {
          alert('Gamertag desvinculado com sucesso!');
          this.fetchUserDetails(this.user!.id);
        },
        error: (err) => {
          alert('Falha ao desvincular o gamertag.');
        },
      });
    }
  }

  openRiotTagOnOpgg(gamerName: string): void {
    const trimmedName = gamerName.trim();
    if (!trimmedName) {
      return;
    }

    const [namePart, tagPart] = trimmedName.split('#');
    const normalizedName = (namePart || '').trim();
    const normalizedTag = (tagPart || 'BR1').trim();
    const riotHandle = `${normalizedName}-${normalizedTag}`;
    const encodedHandle = encodeURIComponent(riotHandle);
    const url = `https://www.op.gg/summoners/br/${encodedHandle}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  saveEmail(): void {
    if (!this.user || this.editableEmail === this.originalEmail) {
      return;
    }

    this.userAlertService
      .updateUserEmail(this.user.id, this.editableEmail)
      .subscribe({
        next: () => {
          alert('E-mail atualizado com sucesso!');
          this.fetchUserDetails(this.user!.id);
        },
        error: (err) => {
          const errorMsg =
            err.error?.email?.[0] || 'Falha ao atualizar o e-mail.';
          alert(errorMsg);
        },
      });
  }

  forcePasswordReset(): void {
    if (!this.user) {
      return;
    }

    const confirmation = confirm(
      `Tem certeza que deseja enviar um e-mail de recuperação de senha para ${this.user.email}?`,
    );
    if (confirmation) {
      this.userAlertService.forcePasswordReset(this.user.email).subscribe({
        next: () => {
          alert('E-mail de recuperação de senha enviado com sucesso!');
        },
        error: (err) => {
          alert('Ocorreu um erro ao tentar enviar o e-mail de recuperação.');
        },
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/dashboard/users-info']);
  }

  openBalanceModal(): void {
    if (!this.user) {
      return;
    }
    this.displayBalanceModal = true;
    this.resetFilters();
    this.currentPage = 1;
    this.userAlertService.retrieveUser(this.user.id).subscribe({
      next: (updatedUser) => {
        this.setUserBalance(updatedUser.coin_amount, updatedUser.gem_amount);
        if (this.user) {
          this.user.coin_amount = updatedUser.coin_amount ?? 0;
          this.user.gem_amount = updatedUser.gem_amount ?? 0;
        }
      },
      error: (err) => {
        if (this.user) {
          this.setUserBalance(this.user.coin_amount, this.user.gem_amount);
        }
      },
    });
    this.loadUserTransactions();
  }
  loadUserTransactions(): void {
    if (!this.user) {
      return;
    }
    const { activeTypes } = this.getActiveFilters();
    this.isLoadingTransactions = true;

    const apiTypes = activeTypes.length > 0 ? activeTypes : undefined;
    this.userAlertService
      .getUserTransactions(this.user.id, this.currentPage, apiTypes)
      .subscribe({
        next: (response: TransactionResponse) => {
          this.transactions = response.results || [];
          this.totalItems = response.count || 0;
          this.isLoadingTransactions = false;
        },
        error: () => {
          this.transactions = [];
          this.totalItems = 0;
          this.isLoadingTransactions = false;
        },
      });
  }
  closeBalanceModal(): void {
    this.displayBalanceModal = false;
    this.currentPage = 1;
  }
  get paginatedTransactions(): Transaction[] {
    return this.transactions;
  }

  get riotActiveGamerName(): ActiveGamerName | null {
    const riotTag = this.user?.active_gamer_names?.RIOT_GAMES;
    if (riotTag) {
      return riotTag;
    }

    const legacyActive = this.user?.active_gamer_name;
    if (!legacyActive) {
      return null;
    }

    if (!legacyActive.type || legacyActive.type === this.RIOT_TYPE) {
      return {
        ...legacyActive,
        type: this.RIOT_TYPE,
      };
    }

    return null;
  }

  get steamActiveGamerName(): ActiveGamerName | null {
    const steamTag = this.user?.active_gamer_names?.STEAM;
    if (steamTag) {
      return steamTag;
    }

    const legacyActive = this.user?.active_gamer_name;
    if (legacyActive?.type === this.STEAM_TYPE) {
      return legacyActive;
    }

    return null;
  }

  get previousRiotGamerNames(): PreviousGamerNameInfo[] {
    return this.getPreviousGamerNamesByType(this.RIOT_TYPE);
  }

  get previousSteamGamerNames(): PreviousGamerNameInfo[] {
    return this.getPreviousGamerNamesByType(this.STEAM_TYPE);
  }

  get sortedAlerts(): UserAlertInfo[] {
    if (!this.user?.alerts?.length) {
      return [];
    }
    return [...this.user.alerts].sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  }

  get sortedNotes(): any[] {
    if (!this.user?.notes?.length) {
      return [];
    }
    return [...this.user.notes].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }

  get hasActiveKYC(): boolean {
    return this.kycList.some((kyc) => kyc.is_active);
  }

  loadUserAlerts(): void {
    const userId = this.user?.id;
    if (!userId) return;

    this.userAlertService.listAlerts(userId).subscribe({
      next: (response: ListAlertResponse) => {
        if (this.user) {
          this.user.alerts = response.alerts;
          this.user.is_limited = response.is_limited;
        }
      },
      error: (err) => {
        console.error('Erro ao recarregar alertas:', err);
      }
    })
  }

  onDeactivateAllAlerts(): void {
    const userId = this.user?.id;
    if (!userId) return;

    if (confirm('Tem certeza que deseja desativar todos os alertas deste usuário?')) {
      this.userAlertService.deactivateAlerts(userId).subscribe({
        next: () => {
          this.loadUserAlerts();
        },
        error: (err) => {
          console.error('Erro ao deletar alertas:', err);
        }
      })
    }
  }

  onDeactivateAlert(alertId: number): void {
    const userId = this.user?.id;
    if (!userId) return;

    this.isLoadingAlert = true;
    this.userAlertService.deactivateAlert(userId, alertId).subscribe({
      next: (response) => {
        this.isLoadingAlert = false;

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Alerta desativado.'
        })

        this.loadUserAlerts();
      },
      error: (err) => {
        this.isLoadingAlert = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível desativar o alerta.'
        });
        console.error(err);
      }
    })
  }

  loadAlertTypes(): void {
    this.userAlertService.getAlertTypes().subscribe({
      next: (types: AlertType[]) => {
        this.alertTypesFromDatabase = types;
        this.alertTypeOptions = types.map(type => ({
          label: `${type.description} (${type.code})`,
          value: type.code
        }))
      },
      error: (err) => {
        console.error('Erro ao buscar tipos de alerta:', err)
      }
    });
  }

  openCreateAlertModal(): void {
    this.newAlert = {
      alert_code: '',
      game: '',
      description: '',
      alert_description: '',
      alert_risk: 1
    };
    this.displayCreateAlertModal = true;
  }

  onAlertTypeChange(event: any): void {
    const selectedCode = event.value;

    const selectedType = this.alertTypesFromDatabase.find(t => t.code === selectedCode);

    if (selectedType) {
      this.newAlert.alert_risk = selectedType.risk;
      this.newAlert.alert_description = selectedType.description;
    }
  }

  onSubmitNewAlert(): void {
    if (!this.newAlert.alert_code || !this.newAlert.game) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro de validação',
        detail: 'Os campos Código do Alerta e Jogo são obrigatórios.'
      });
      return;
    }

    const id = this.user?.id;
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível identificar o ID do usuário atual.'
      });
      return;
    }

    this.isSubmittingAlert = true;

    this.userAlertService.createAlert(id, this.newAlert).subscribe({
      next: () => {
        this.isSubmittingAlert = false;
        this.displayCreateAlertModal = false;

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'O alerta foi registrado e vinculado ao usuário.'
        });

        this.loadUserAlerts();
      },
      error: (err) => {
        this.isSubmittingAlert = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro na API',
          detail: 'O servidor rejeitou a criação do alerta. Verifique as regras do modelo.'
        });
        console.error('Erro ao submeter alerta:', err);
      }
    })
  }

  getAlertSeverity(
    alertDescription: string | null,
  ): 'high' | 'medium' | 'info' {
    const description = (alertDescription ?? '').toLowerCase();
    const highRiskTerms = ['fraude', 'chargeback', 'bloqueio', 'suspeita'];
    const mediumRiskTerms = ['inconsist', 'verifica', 'falha recorrente'];

    if (highRiskTerms.some((term) => description.includes(term))) {
      return 'high';
    }
    if (mediumRiskTerms.some((term) => description.includes(term))) {
      return 'medium';
    }
    return 'info';
  }

  getAlertSeverityLabel(alertDescription: string): string {
    const severity = this.getAlertSeverity(alertDescription);
    if (severity === 'high') return 'Alta';
    if (severity === 'medium') return 'Media';
    return 'Info';
  }

  hasActiveFilters(): boolean {
    return (
      this.filterConversion ||
      this.filterBattlePrize ||
      this.filterBattleCost ||
      this.filterBattlePassReward ||
      this.filterHeroPurchase ||
      this.filterCashOut ||
      this.filterDeposits
    );
  }
  scrollToTop(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = 0;
    }
  }
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePageData();
    }
  }
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePageData();
    }
  }
  onFilterChange(): void {
    if (this.hasActiveFilters()) {
      if (this.currentPage > 1 && this.previousPageBeforeFilter === 1) {
        this.previousPageBeforeFilter = this.currentPage;
      }
      this.currentPage = 1;
    } else {
      this.currentPage = this.previousPageBeforeFilter;
      this.previousPageBeforeFilter = 1;
    }
    this.loadUserTransactions();
    setTimeout(() => this.scrollToTop(), 0);
  }
  get filteredItemsCount(): number {
    return this.totalItems;
  }
  get totalPages(): number {
    return Math.ceil(this.filteredItemsCount / this.itemsPerPage);
  }

  openNoteModal(): void {
    this.editingNoteId = null;
    this.newNoteText = '';
    this.displayNoteModal = true;
  }

  openEditNoteModal(note: any): void {
    this.editingNoteId = note.id;
    this.newNoteText = note.description;
    this.displayNoteModal = true;
  }

  closeNoteModal(): void {
    this.displayNoteModal = false;
    this.editingNoteId = null;
    this.newNoteText = '';
  }

  saveNote(): void {
    if (!this.user || !this.newNoteText.trim()) return;

    this.isSavingNote = true;
    const isEditing = this.editingNoteId !== null;

    const request$ = this.editingNoteId
      ? this.userAlertService.updateAccountNote(
          this.editingNoteId,
          this.newNoteText.trim(),
        )
      : this.userAlertService.createAccountNote(
          this.user.id,
          this.newNoteText.trim(),
        );

    request$.subscribe({
      next: () => {
        this.isSavingNote = false;
        this.closeNoteModal();
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: isEditing
            ? 'Nota atualizada com sucesso!'
            : 'Nota adicionada com sucesso!',
        });
        this.fetchUserDetails(this.user!.id);
      },
      error: (err) => {
        this.isSavingNote = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao salvar a nota.',
        });
      },
    });
  }

  deleteNote(noteId: number): void {
    if (confirm('Tem certeza que deseja deletar esta nota?')) {
      this.userAlertService.deleteAccountNote(noteId).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Nota deletada com sucesso!',
          });
          this.fetchUserDetails(this.user!.id);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao deletar a nota.',
          });
        },
      });
    }
  }

  restartKYCProcess(): void {
    const id = this.user!.id;

    if (
      confirm(
        'Tem certeza que deseja reiniciar o processo de KYC para este usuário? Isso desativará a verificação KYC atual.',
      )
    ) {
      this.accountService.deactivateKYC(id).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Processo de KYC reiniciado com sucesso!',
          });
          this.fetchKYCVerifications(id);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao reiniciar o processo de KYC.',
          });
          console.error('Erro ao desativar KYC:', err);
        },
      });
    }
  }

  private getPreviousGamerNamesByType(
    type: GamerNameType,
  ): PreviousGamerNameInfo[] {
    if (!this.user?.previous_gamer_names?.length) {
      return [];
    }

    const filtered = this.user.previous_gamer_names.filter((item) => {
      const resolvedType =
        item.type ?? this.inferLegacyGamerType(item.gamer_name);
      return resolvedType === type;
    });

    return filtered.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }

  private inferLegacyGamerType(gamerName: string): GamerNameType {
    return gamerName.includes('#') ? this.RIOT_TYPE : this.STEAM_TYPE;
  }

  private resetFilters(): void {
    this.filterConversion = false;
    this.filterBattlePrize = false;
    this.filterBattleCost = false;
    this.filterBattlePassReward = false;
    this.filterHeroPurchase = false;
    this.filterCashOut = false;
    this.filterDeposits = false;
  }
  private setUserBalance(coinAmount?: number, gemAmount?: number): void {
    this.userBalance = {
      coins: coinAmount ?? 0,
      diamonds: gemAmount ?? 0,
    };
  }
  private getActiveFilters(): { activeTypes: string[] } {
    const activeTypes: string[] = [];
    if (this.filterConversion) {
      activeTypes.push('CURRENCY_CONVERSION');
    }
    if (this.filterBattlePrize) {
      activeTypes.push('BATTLE_PRIZE');
    }
    if (this.filterBattleCost) {
      activeTypes.push('BATTLE_COST');
      activeTypes.push('BATTLE_REFUND');
    }
    if (this.filterBattlePassReward) {
      activeTypes.push('BATTLE_PASS_REWARD');
    }
    if (this.filterHeroPurchase) {
      activeTypes.push('HERO_PURCHASE');
    }
    if (this.filterCashOut) {
      activeTypes.push('CASH_OUT');
    }
    if (this.filterDeposits) {
      activeTypes.push('DIAMOND_PURCHASE');
      activeTypes.push('COIN_PURCHASE');
    }
    return { activeTypes };
  }
  private updatePageData(): void {
    this.loadUserTransactions();
    setTimeout(() => this.scrollToTop(), 0);
  }
}
